import { useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePendingOrderAmendmentQueue, useApproveOrderAmendment, useRejectOrderAmendment, useApplyOrderAmendment } from "../hooks/useOrders";
import type { OrderAmendmentQueueItem } from "../services/ordersApi";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { normalizeError } from "@/shared/errors/normalizeError";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { PaginationBar } from "@/components/ui/PaginationBar";

type QueueAmendment = OrderAmendmentQueueItem;

const formatDate = (iso?: string) => {
  if (!iso) return "-";
  try {
    return format(parseISO(iso), "dd MMM yyyy HH:mm");
  } catch {
    return "-";
  }
};

export const OrderAmendmentApprovalPage = () => {
  const { hasPermission } = useAuthStore();
  const canApprove = hasPermission("orders.amend_approve");
  const canApply = hasPermission("orders.amend_apply");

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const queueQuery = usePendingOrderAmendmentQueue(pageNumber, pageSize);

  const approveMutation = useApproveOrderAmendment();
  const rejectMutation = useRejectOrderAmendment();
  const applyMutation = useApplyOrderAmendment();

  const rows = useMemo(() => queueQuery.data?.items ?? [], [queueQuery.data]);
  const [rejectTarget, setRejectTarget] = useState<QueueAmendment | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<string>>(() => new Set());
  const applyingIdRef = useRef<string | null>(null);
  const isBusy = approveMutation.isPending || rejectMutation.isPending || applyMutation.isPending || applyingId !== null;

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when page size changes
  };

  const onApprove = async (row: QueueAmendment) => {
    try {
      const result = await approveMutation.mutateAsync({
        orderId: row.orderId,
        amendmentId: row.id,
        payload: { approvalNotes: "Approved from queue" },
      });
      if (result.warnings.length > 0)
        toast.warning(`Amendment applied, but accounting needs attention: ${result.warnings.join(" ")}`);
      else
        toast.success("Amendment approved and applied");
      queueQuery.refetch();
    } catch (error) {
      const env = normalizeError(error);
      toast.error(env.userMessage || "Failed to approve and apply amendment");
    }
  };

  const openRejectDialog = (row: QueueAmendment) => {
    setRejectTarget(row);
    setRejectReason("");
  };

  const onReject = async () => {
    if (!rejectTarget) return;

    const reason = rejectReason.trim();
    if (!reason) {
      toast.error("Rejection reason is required");
      return;
    }

    if (reason.length > 500) {
      toast.error("Rejection reason cannot exceed 500 characters");
      return;
    }

    try {
      await rejectMutation.mutateAsync({
        orderId: rejectTarget.orderId,
        amendmentId: rejectTarget.id,
        payload: { rejectionReason: reason },
      });
      toast.success("Amendment rejected");
      setRejectTarget(null);
      setRejectReason("");
      queueQuery.refetch();
    } catch (error) {
      const env = normalizeError(error);
      toast.error(env.userMessage || "Failed to reject amendment");
    }
  };

  const onApply = async (row: QueueAmendment) => {
    if (isBusy || applyingIdRef.current || appliedIds.has(row.id)) return;
    applyingIdRef.current = row.id;
    setApplyingId(row.id);
    try {
      await applyMutation.mutateAsync({
        orderId: row.orderId,
        amendmentId: row.id,
        payload: { notes: "Applied from queue" },
      });
      setAppliedIds((current) => new Set(current).add(row.id));
      toast.success("Amendment applied");
      await queueQuery.refetch();
    } catch (error) {
      const env = normalizeError(error);
      toast.error(env.userMessage || "Failed to apply amendment");
    } finally {
      applyingIdRef.current = null;
      setApplyingId(null);
    }
  };

  if (queueQuery.isLoading) {
    return <div className="p-6">Loading order amendment queue...</div>;
  }

  if (queueQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load amendment queue</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Order Amendment Queue</h1>
          <p className="text-muted-foreground mt-1">
            Approve to apply an amendment immediately. Previously approved amendments remain available to apply.
          </p>
        </div>
        <Button variant="outline" onClick={() => queueQuery.refetch()}>
          Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
          No pending order amendments.
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Amendment #</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Order Status</TableHead>
                <TableHead className="text-right">Delta</TableHead>
                <TableHead className="text-right">Transport</TableHead>
                <TableHead>Requested At</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: QueueAmendment) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">{row.amendmentNumber}</TableCell>
                  <TableCell className="font-mono text-xs">{row.orderNumber}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>
                    <Badge variant={row.status === "Approved" ? "default" : "secondary"}>{row.status}</Badge>
                  </TableCell>
                  <TableCell>{row.orderStatus}</TableCell>
                  <TableCell className="text-right">
                    <span className={row.deltaAmount < 0 ? "text-emerald-700" : "text-amber-700"}>
                      {row.deltaAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-xs">
                    <span>{Number(row.oldTransportCost ?? 0).toLocaleString()}</span>
                    <span className="mx-1 text-muted-foreground">→</span>
                    <span>{Number(row.newTransportCost ?? 0).toLocaleString()}</span>
                    {row.newFulfilmentMethod === "CustomerCollection" && (
                      <span className="block text-muted-foreground">Customer pickup</span>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(row.requestedAt)}</TableCell>
                  <TableCell className="max-w-[260px] truncate">{row.reason}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {row.status === "Submitted" && canApprove && (
                      <>
                        <Button size="sm" disabled={isBusy} onClick={() => onApprove(row)}>
                          Approve
                        </Button>
                        <Button size="sm" variant="destructive" disabled={isBusy} onClick={() => openRejectDialog(row)}>
                          Reject
                        </Button>
                      </>
                    )}
                    {row.status === "Approved" && canApply && (
                      <Button size="sm" disabled={isBusy || appliedIds.has(row.id)} onClick={() => onApply(row)} aria-busy={applyingId === row.id}>
                        {applyingId === row.id ? (
                          <><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Applying...</>
                        ) : appliedIds.has(row.id) ? "Applied" : "Apply"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {queueQuery.data && queueQuery.data.totalCount > 0 && (
        <PaginationBar
          currentPage={queueQuery.data.currentPage}
          pageSize={queueQuery.data.pageSize}
          totalCount={queueQuery.data.totalCount}
          totalPages={queueQuery.data.totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <Dialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRejectTarget(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject order amendment</DialogTitle>
            <DialogDescription>
              {rejectTarget
                ? `${rejectTarget.amendmentNumber} - ${rejectTarget.orderNumber}`
                : "Provide a reason for rejecting this amendment."}
            </DialogDescription>
          </DialogHeader>

          <Field>
            <FieldLabel>
              Rejection reason <span className="text-destructive">*</span>
            </FieldLabel>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="State why this amendment is being rejected"
              rows={4}
              maxLength={500}
              disabled={rejectMutation.isPending}
            />
          </Field>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason("");
              }}
              disabled={rejectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onReject}
              disabled={rejectMutation.isPending || !rejectReason.trim()}
            >
              {rejectMutation.isPending ? "Rejecting..." : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
